import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { getAction } from '../actions/getters';
import { getContext } from '../contexts/getters';
import { useEdgesList } from '../edges/getters';
import {
	element,
	isNativeElement,
	nativeElement,
	uiElement,
	selectedWorkflowId,
	getNodeByUuidForSelectedWorkflowVersion,
	getNodeTestResultByUuidForSelectedWorkflowVersion,
} from '../selectedWorkflow/getters';
import { getSubflowInputFields } from '../subflows/getters';
import { getConfigurationResponse } from '../workflowConfigurations/getters';
import { user as userGetter } from '@/store/modules/auth/getters';
import { allWorkflows, rootNodeId as rootNodeIdGetter } from '@/store/modules/workflows/getters';
import AppActionService from '@/utils/AppActionService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { GLOBAL_RUN_DATA } from '@/utils/workflow-node.const';

const buildNodeGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const data = buildNodeGetterObject('data');
export const getNode = buildNodeGetterObject('getNode');
export const getNodeByUuidForWorkflowVersion = buildNodeGetterObject('getNodeByUuidForWorkflowVersion');
export const getNodeTestResult = buildNodeGetterObject('getNodeTestResult');
export const previousStepsData = buildNodeGetterObject('previousStepsData');
export const getPreviousStepDataForNode = buildNodeGetterObject('getPreviousStepDataForNode');
export const getPreviousStepDataForNodeByUUID = buildNodeGetterObject('getPreviousStepDataForNodeByUUID');
export const getPreviousStepNodeData = buildNodeGetterObject('getPreviousStepNodeData');
export const loadingTestAndContinue = buildNodeGetterObject('loadingTestAndContinue');
export const editNodeLink = buildNodeGetterObject('editNodeLink');
export const getNodeOutputLabelsByNodeId = buildNodeGetterObject('getNodeOutputLabelsByNodeId');
export const getGlobalWorkflowData = buildNodeGetterObject('getGlobalWorkflowData');
export const getGlobalWorkflowDataWithKey = buildNodeGetterObject('getGlobalWorkflowDataWithKey');
export const useNodeEdges = buildNodeGetterObject('useNodeEdges');
export const useNodeIncomingEdges = buildNodeGetterObject('useNodeIncomingEdges');
export const useNodeOutgoingEdges = buildNodeGetterObject('useNodeOutgoingEdges');
export const useIsRootNode = buildNodeGetterObject('useIsRootNode');
export const isEndRunWidget = buildNodeGetterObject('isEndRunWidget');
export const isWidget = buildNodeGetterObject('isWidget');

const getters = {
	[data.getter]: (state) => state.data,
	[getNode.getter]: (state) => (nodeId) => state.data[nodeId],
	[getNodeByUuidForWorkflowVersion.getter]: (state) => (nodeUUID, workflowVersionId) => {
		const nodes = Object.values(state.data).filter((node) => node?.workflow_version === workflowVersionId);
		return nodes.find((node) => node.uuid === nodeUUID);
	},
	[useIsRootNode.getter]: (state, getters, rootState, rootGetters) => (nodeId) => {
		const node = getters[getNode.getter](nodeId);
		if (!node) {
			return false;
		}
		const rootNodeId = rootGetters[rootNodeIdGetter.fullPath](node.workflow, node.workflow_version);
		return node.id === rootNodeId;
	},
	[useNodeEdges.getter]: (state, getters, rootState, rootGetters) => (nodeId) =>
		rootGetters[useEdgesList.fullPath].filter((edge) => edge.source === nodeId || edge.target === nodeId),
	[useNodeIncomingEdges.getter]: (state, getters) => (nodeId) =>
		getters[useNodeEdges.getter](nodeId).filter((edge) => edge.target === nodeId),
	[useNodeOutgoingEdges.getter]: (state, getters) => (nodeId) =>
		getters[useNodeEdges.getter](nodeId)
			.filter((edge) => edge.source === nodeId)
			.toSorted((a, b) => (a.branch_path?.order || 0) - (b.branch_path?.order || 0)),
	[getNodeTestResult.getter]:
		(state, getters) =>
		(nodeId, cleanHiddenFields = true) => {
			const nodeData = getters[getNode.getter](nodeId);
			return WorkflowNodeService.getNodeTestResult(nodeData, cleanHiddenFields);
		},
	[getGlobalWorkflowData.getter]:
		(state, getters, rootState, rootGetters) =>
		(workflowId, withConfigurations = true, backwardComp = false) =>
			WorkflowNodeService.getGlobalWorkflowRunData(
				rootGetters[allWorkflows.fullPath].find((x) => x.id == workflowId),
				rootGetters[userGetter.fullPath],
				rootGetters[getConfigurationResponse.fullPath],
				withConfigurations,
				backwardComp
			),
	[getGlobalWorkflowDataWithKey.getter]:
		(state, getters) =>
		(workflowId, withConfigurations = true, backwardComp = false) => ({
			[GLOBAL_RUN_DATA]: getters[getGlobalWorkflowData.getter](workflowId, withConfigurations, backwardComp),
		}),
	[getPreviousStepDataForNode.getter]:
		(state, getters) =>
		(nodeId, backwardComp = true) => {
			const node = getters[getNode.getter](nodeId);
			const nodeTestResult = { ...getters[getNodeTestResult.getter](nodeId) };
			return getters[getPreviousStepNodeData.getter](node, nodeTestResult, false, backwardComp);
		},
	[getPreviousStepDataForNodeByUUID.getter]:
		(state, getters, rootState, rootGetters) =>
		(nodeUUID, backwardComp = true) => {
			// NOTICE: this uses the selected workflow version to get the correct node by uuid (in the selected workflow version)
			const node = rootGetters[getNodeByUuidForSelectedWorkflowVersion.fullPath](nodeUUID);
			const nodeTestResult = { ...rootGetters[getNodeTestResultByUuidForSelectedWorkflowVersion.fullPath](nodeUUID) };
			return getters[getPreviousStepNodeData.getter](node, nodeTestResult, true, backwardComp);
		},
	[getPreviousStepNodeData.getter]:
		(state, getters, rootState, rootGetters) =>
		(node, nodeTestResult, includeTestValues = false, backwardComp = true) => {
			if (!node) {
				return {};
			}
			// these are the root node of the selected workflow properties
			const selectedWorkflowElement = rootGetters[element.fullPath];
			const rootNodeGetters = {
				element: selectedWorkflowElement,
				elementContext: rootGetters[getContext.fullPath](selectedWorkflowElement?.context),
				uiElement: rootGetters[uiElement.fullPath],
				isNativeElement: rootGetters[isNativeElement.fullPath],
				nativeElement: rootGetters[nativeElement.fullPath],
				getConfigurationResponse: rootGetters[getConfigurationResponse.fullPath],
				getSubflowInputFields: rootGetters[getSubflowInputFields.fullPath],
			};
			return WorkflowNodeService.getPreviousNodeData(
				node,
				nodeTestResult,
				rootGetters[allWorkflows.fullPath],
				rootGetters[userGetter.fullPath],
				rootNodeGetters,
				includeTestValues,
				backwardComp,
				getters[useIsRootNode.getter](node.id)
			);
		},
	// key is uuid of node
	[previousStepsData.getter]: (state, getters, rootState, rootGetters) => {
		const allPreviousSteps = Object.values(getters[data.getter]).reduce(
			(allPreviousSteps, node) => ({
				...allPreviousSteps,
				[node.uuid]: getters[getPreviousStepDataForNodeByUUID.getter](node.uuid),
			}),
			{}
		);
		if (rootGetters[selectedWorkflowId.fullPath]) {
			return { ...getters[getGlobalWorkflowDataWithKey.getter](rootGetters[selectedWorkflowId.fullPath]), ...allPreviousSteps };
		}
		return allPreviousSteps;
	},
	[loadingTestAndContinue.getter]: (state) => state.loadingTestAndContinue,
	[editNodeLink.getter]:
		() =>
		({ projectId, flowId, nodeId, versionId = null }) =>
			versionId
				? `/space/${projectId}/feature/${flowId}/version/${versionId}/nodes/${nodeId}`
				: `/space/${projectId}/feature/${flowId}/nodes/${nodeId}`,
	[getNodeOutputLabelsByNodeId.getter]: (state, getters) => (nodeId) =>
		WorkflowNodeService.getTestResponseOutputLabels(getters[getNode.getter](nodeId)),
	[isEndRunWidget.getter]: (state, getters, rootState, rootGetters) => (nodeId) => {
		const node = getters[getNode.getter](nodeId);
		const action = rootGetters[getAction.fullPath](node?.action);
		return node?.action && WorkflowNodeService.isEndRun(node) && AppActionService.isActionOfEndRunWidget(action);
	},
	[isWidget.getter]: (state, getters) => (nodeId) => {
		const node = getters[getNode.getter](nodeId);
		return node && WorkflowNodeService.isWidget(node);
	},
};

export default getters;
